/**
 * A saída do exame para quem consulta o caso (E91.F2, GUS-51 RF05/RN01).
 *
 * "Baixar exame" é descoberta, não regra nova: o documento já existe — o
 * relatório da análise preliminar que o portal entrega, com o gate
 * `exame_entregue` por peça. A preliminar e a mesa do perito o pedem pela PORTA
 * DO PERITO (`GET /api/casos/{id}/exame-eletronico/relatorio-preliminar.pdf`,
 * E91.F3.T05): a rota do portal recusa a plataforma por desenho, e é a
 * plataforma quem examina os casos de cliente — o smoke real da E91 achou o
 * 403. Mesma montagem, mesmo render, mesmo gate; nenhuma regra de entrega muda
 * por uma decisão de tela.
 *
 * A disponibilidade espelha a régua do servidor (`_resultado_eletronico` em
 * `exame_primario_service.py`): uma peça está entregue quando o exame dela
 * tem `tendencia` e não é rascunho. A tela só antecipa a resposta para dizer
 * o MOTIVO antes do clique — o servidor continua decidindo no download.
 */
import axios, { type AxiosInstance } from 'axios';

import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { codigoDeVerificacao, nomeDoAnexo, type ArquivoBaixado } from '@/lib/http_download';
import { rehydrateBlobError } from '@/lib/laudo';

export type MotivoIndisponivel = 'sem_documentos' | 'pendente_de_verificacao' | 'em_processamento';

export type DisponibilidadeDoExame =
  | { disponivel: true }
  | { disponivel: false; motivo: MotivoIndisponivel };

/** A peça conta como entregue para o servidor? A mesma régua de `exame_entregue`. */
export function pecaEntregue(peca: ExameEletronicoCcbData): boolean {
  const exame = peca.exame;
  return exame !== null && exame.tendencia !== null && exame.status !== 'rascunho';
}

export function disponibilidadeDoExame(
  pecas: readonly ExameEletronicoCcbData[],
  emProcessamento = false,
): DisponibilidadeDoExame {
  if (pecas.length === 0) return { disponivel: false, motivo: 'sem_documentos' };
  if (emProcessamento) return { disponivel: false, motivo: 'em_processamento' };
  if (!pecas.every(pecaEntregue)) return { disponivel: false, motivo: 'pendente_de_verificacao' };
  return { disponivel: true };
}

export type SaidaDoExameErrorReason =
  | 'nao_disponivel'
  /** E111.F1 (S-03): peça em trabalho verificada com a régua anterior; verificar de novo. */
  | 'regua_anterior'
  /** E140.F2 (GUS-143): a leitura dos documentos tem falha técnica em conferência. */
  | 'leitura_em_conferencia'
  | 'not_found'
  | 'forbidden'
  | 'network'
  | 'unknown';

export class SaidaDoExameError extends Error {
  public readonly reason: SaidaDoExameErrorReason;
  /** E111.F1 (S-03): em `regua_anterior`, as peças a verificar de novo. */
  public readonly pendentes: readonly string[];

  constructor(reason: SaidaDoExameErrorReason, pendentes: readonly string[] = []) {
    super(`saida_do_exame_error:${reason}`);
    this.name = 'SaidaDoExameError';
    this.reason = reason;
    this.pendentes = pendentes;
  }
}

const RECUSA_COM_PENDENTES: Record<string, SaidaDoExameErrorReason> = {
  REGUA_ANTERIOR: 'regua_anterior',
  LEITURA_EM_CONFERENCIA: 'leitura_em_conferencia',
};

/** A recusa estruturada da porta do perito, que nomeia o que resolver: a peça verificada com a
 * régua anterior (E111.F1). A de imagem sem leitura confirmada (E110.F1) saiu na E113.F2. */
function recusaComPendentes(data: unknown): SaidaDoExameError | null {
  const detail = (data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail !== 'object' || detail === null) return null;
  const { code, pendentes } = detail as { code?: unknown; pendentes?: unknown };
  const reason = typeof code === 'string' ? RECUSA_COM_PENDENTES[code] : undefined;
  if (reason === undefined || !Array.isArray(pendentes)) return null;
  return new SaidaDoExameError(
    reason,
    pendentes.filter((p): p is string => typeof p === 'string'),
  );
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;

/**
 * Traduz a recusa do servidor para o vocabulário da tela.
 *
 * O 422 é o gate de entrega (`RELATORIO_DO_CLIENTE_NOT_READY`): a tela já
 * antecipa a condição, mas a resposta do servidor é a que vale — entre o
 * carregamento e o clique alguém pode ter reaberto uma peça.
 */
export function classifySaidaDoExameError(err: unknown): SaidaDoExameError {
  if (err instanceof SaidaDoExameError) return err;
  if (!axios.isAxiosError(err)) return new SaidaDoExameError('unknown');
  const status = err.response?.status;
  if (status === undefined) return new SaidaDoExameError('network');
  if (status === HTTP_UNPROCESSABLE) {
    return recusaComPendentes(err.response?.data) ?? new SaidaDoExameError('nao_disponivel');
  }
  if (status === HTTP_FORBIDDEN) return new SaidaDoExameError('forbidden');
  if (status === HTTP_NOT_FOUND) return new SaidaDoExameError('not_found');
  return new SaidaDoExameError('unknown');
}

export function nomeDoArquivoDoExame(caseId: string): string {
  return `exame-${caseId}.pdf`;
}

/**
 * Tempo de espera desta chamada, e não o padrão do cliente.
 *
 * O documento é re-renderizado sob demanda (docx → conversor headless) e leva
 * dezenas de segundos; com o padrão de 15 s o navegador desistiria enquanto o
 * servidor ainda converte, e a tela diria "falhou" a um download que deu certo
 * (mesma lição do processamento inicial, E82.F5).
 */
export const BAIXAR_EXAME_TIMEOUT_MS = 180_000;

export async function baixarExame(client: AxiosInstance, caseId: string): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(
      `/casos/${caseId}/exame-eletronico/relatorio-preliminar.pdf`,
      {
        responseType: 'blob',
        timeout: BAIXAR_EXAME_TIMEOUT_MS,
      },
    );
    const headers = resposta.headers as Record<string, unknown>;
    return {
      blob: resposta.data,
      // O nome é o que o servidor mandou (GUS-18): o título do documento da modalidade.
      filename: nomeDoAnexo(headers['content-disposition'], nomeDoArquivoDoExame(caseId)),
      codigoVerificacao: codigoDeVerificacao(headers),
    };
  } catch (err) {
    // A resposta é pedida como blob: a recusa estruturada (E110.F1) chega como blob também.
    throw classifySaidaDoExameError(await rehydrateBlobError(err));
  }
}
